import { useState, useCallback, useMemo, useEffect } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
  Panel,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Database,
  FileSpreadsheet,
  Globe,
  LayoutDashboard,
  PieChart,
  AlertTriangle,
  Users,
  Download,
  Trash2,
  Clock,
  TrendingUp,
  Layers,
  Mail,
  Server,
  Cpu,
  Eye,
  EyeOff,
  Maximize2,
  Info,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────
//  NODE TYPE CONFIG
// ─────────────────────────────────────────────
const NODE_STYLES = {
  source: {
    bg: "#ffffff",
    border: "#bfdbfe",
    text: "#1e3a5f",
    accent: "#2563eb",
    icon: FileSpreadsheet,
    label: "Data Source",
  },
  email: {
    bg: "#ffffff",
    border: "#ddd6fe",
    text: "#4c1d95",
    accent: "#7c3aed",
    icon: Mail,
    label: "Email Scanner",
  },
  external: {
    bg: "#ffffff",
    border: "#99f6e4",
    text: "#134e4a",
    accent: "#0d9488",
    icon: Globe,
    label: "External API",
  },
  processing: {
    bg: "#ffffff",
    border: "#fed7aa",
    text: "#78350f",
    accent: "#d97706",
    icon: Cpu,
    label: "Processing",
  },
  collection: {
    bg: "#ffffff",
    border: "#bbf7d0",
    text: "#14532d",
    accent: "#16a34a",
    icon: Database,
    label: "Collection",
  },
  page: {
    bg: "#f0fdfa",
    border: "#5eead4",
    text: "#134e4a",
    accent: "#0d9488",
    icon: LayoutDashboard,
    label: "App Page",
  },
  cronjob: {
    bg: "#ffffff",
    border: "#e9d5ff",
    text: "#581c87",
    accent: "#9333ea",
    icon: Clock,
    label: "Cron Job",
  },
};

// ─────────────────────────────────────────────
//  CUSTOM NODE COMPONENT
// ─────────────────────────────────────────────
function FlowNode({ data, selected }) {
  const style = NODE_STYLES[data.nodeType] || NODE_STYLES.collection;
  const Icon = data.icon || style.icon;
  const isHighlighted = data.highlighted;
  const isDimmed = data.dimmed;

  return (
    <div
      data-testid={`flow-node-${data.id}`}
      className="relative transition-all duration-300"
      style={{
        opacity: isDimmed ? 0.2 : 1,
        transform: isHighlighted ? "scale(1.05)" : "scale(1)",
        filter: isHighlighted ? `drop-shadow(0 0 8px ${style.accent}50)` : "none",
      }}
    >
      {data.hasInput && (
        <Handle
          type="target"
          position={Position.Left}
          style={{ background: style.accent, width: 8, height: 8, border: "2px solid #fff" }}
        />
      )}
      <div
        style={{
          background: style.bg,
          border: `2px solid ${isHighlighted ? style.accent : style.border}`,
          borderRadius: 10,
          padding: "10px 16px",
          minWidth: data.wide ? 200 : 160,
          maxWidth: 240,
          cursor: "pointer",
          boxShadow: isHighlighted ? `0 4px 12px ${style.accent}25` : "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <div className="flex items-center gap-2 mb-1">
          <Icon size={14} style={{ color: style.accent, flexShrink: 0 }} />
          <span
            style={{ color: style.text, fontSize: 12, fontWeight: 600, lineHeight: 1.2 }}
            className="truncate"
          >
            {data.label}
          </span>
        </div>
        {data.sublabel && (
          <span style={{ color: "#64748b", fontSize: 10, display: "block", lineHeight: 1.2 }}>
            {data.sublabel}
          </span>
        )}
        {data.count !== undefined && (
          <div className="mt-1">
            <span
              style={{
                color: style.accent,
                fontSize: 10,
                fontWeight: 700,
                background: `${style.accent}12`,
                padding: "1px 6px",
                borderRadius: 4,
              }}
            >
              {typeof data.count === "number" ? data.count.toLocaleString() + " records" : data.count}
            </span>
          </div>
        )}
      </div>
      {data.hasOutput && (
        <Handle
          type="source"
          position={Position.Right}
          style={{ background: style.accent, width: 8, height: 8, border: "2px solid #fff" }}
        />
      )}
    </div>
  );
}

const nodeTypes = { flowNode: FlowNode };

// ─────────────────────────────────────────────
//  FULL DATA MODEL  (nodes + edges)
// ─────────────────────────────────────────────
function buildGraph(collectionCounts) {
  const c = (name) => collectionCounts[name] ?? "...";

  // ── COLUMN 1 : Data Sources ──
  const col1X = 0;
  const sources = [
    { id: "karvy_mfsd201", label: "MFSD201", sublabel: "Karvy Transactions", count: c("MFSD201"), y: 0 },
    { id: "karvy_mfsd203", label: "MFSD203", sublabel: "Karvy Folios", count: c("MFSD203"), y: 80 },
    { id: "karvy_mfsd211", label: "MFSD211", sublabel: "Karvy Holdings", count: c("MFSD211"), y: 160 },
    { id: "cams_wbr2", label: "WBR2", sublabel: "CAMS Transactions", count: c("WBR2"), y: 280 },
    { id: "cams_wbr2a", label: "WBR2A", sublabel: "CAMS Historical", count: c("WBR2A"), y: 360 },
    { id: "cams_wbr9", label: "WBR9", sublabel: "CAMS Holdings", count: c("WBR9"), y: 440 },
    { id: "cams_wbr9d", label: "WBR9D", sublabel: "CAMS Dividends", count: c("WBR9D"), y: 520 },
  ];

  const emails = [
    { id: "karvy_email", label: "Karvy Email", sublabel: "IMAP Scanner", y: 620, count: "Auto-fetch" },
    { id: "cams_email", label: "CAMS Email", sublabel: "IMAP Scanner", y: 700, count: "Auto-fetch" },
  ];

  const externals = [
    { id: "bse_api", label: "BSE StarMF", sublabel: "Scheme Master Feed", y: 800, count: c("bse_scheme_raw") },
    { id: "amfi_api", label: "AMFI India", sublabel: "Category Data", y: 880, count: c("scheme_data") },
    { id: "mfapi", label: "mfapi.in", sublabel: "NAV & Face Value", y: 960, count: "API" },
  ];

  // ── COLUMN 2 : Processing / Mappings ──
  const col2X = 320;
  const processing = [
    { id: "column_mapping", label: "Column Mapping", sublabel: "Schema Normalization", y: 60, count: c("column_mapping_config") },
    { id: "flag_mapping", label: "Flag Mapping", sublabel: "Txn Type Classification", y: 160 },
    { id: "raw_ingestion", label: "Raw Ingestion", sublabel: "Chunked Upload Pipeline", y: 300 },
    { id: "email_processing", label: "Email Processing", sublabel: "DBF/Attachment Parser", y: 660 },
  ];

  // ── COLUMN 3 : Master Collections ──
  const col3X = 640;
  const collections = [
    { id: "investor_master", label: "investor_master", sublabel: "Unified Client + Folio Data", count: c("investor_master"), y: 0 },
    { id: "aum_master", label: "aum_master", sublabel: "AUM per Folio + Scheme", count: c("aum_master"), y: 100 },
    { id: "transaction_master", label: "transaction_master", sublabel: "All Financial Transactions", count: c("transaction_master"), y: 200 },
    { id: "scheme_master", label: "scheme_master", sublabel: "Fund Scheme Directory", count: c("scheme_master"), y: 320 },
    { id: "nav_master", label: "nav_master", sublabel: "Historical & Daily NAV", count: c("nav_master"), y: 520 },
    { id: "scheme_data", label: "scheme_data", sublabel: "AMFI Category Enrichment", count: c("scheme_data"), y: 620 },
    { id: "bse_scheme_raw", label: "bse_scheme_raw", sublabel: "BSE Raw Scheme Data", count: c("bse_scheme_raw"), y: 720 },
    { id: "kyc_mismatch", label: "kyc_mismatch", sublabel: "PAN Mismatch Records", count: c("kyc_mismatch"), y: 840 },
    { id: "kyc_missing", label: "kyc_missing", sublabel: "Missing KYC Records", count: c("kyc_missing"), y: 930 },
  ];

  // ── COLUMN 4 : Cron Jobs ──
  const col4X = 960;
  const cronjobs = [
    { id: "cron_nav", label: "NAV Update Bot", sublabel: "Daily + Historical NAV", y: 500 },
    { id: "cron_bse", label: "BSE Scheme Master", sublabel: "2:15 AM IST (bulk_write)", y: 600 },
    { id: "cron_amfi", label: "AMFI Category", sublabel: "2:00 AM IST", y: 700 },
    { id: "cron_recon", label: "Reconciliation", sublabel: "Unit Mismatch Detection", y: 820 },
    { id: "cron_karvy_feed", label: "Karvy Feed Bot", sublabel: "Selenium Automation", y: 150 },
    { id: "cron_cams_feed", label: "CAMS Feed Bot", sublabel: "Selenium Automation", y: 250 },
  ];

  // ── COLUMN 5 : Pages ──
  const col5X = 1280;
  const pages = [
    { id: "page_dashboard", label: "Dashboard", sublabel: "Client Overview & Analytics", y: 0, icon: LayoutDashboard },
    { id: "page_client_wise", label: "Client Wise", sublabel: "Per-Client Summary", y: 100, icon: Users },
    { id: "page_portfolio", label: "Portfolio", sublabel: "Holdings, Txns, Active Units", y: 220, icon: PieChart },
    { id: "page_mismatch", label: "Mismatch", sublabel: "KYC / Unit Reconciliation", y: 380, icon: AlertTriangle },
    { id: "page_research", label: "Research", sublabel: "MF Scheme Analytics", y: 500, icon: TrendingUp },
    { id: "page_cronjobs", label: "Cron Jobs", sublabel: "Bot Scheduling UI", y: 630, icon: Clock },
    { id: "page_masters", label: "Masters", sublabel: "Upload & Reference Data", y: 750, icon: Database },
    { id: "page_downloads", label: "Downloads", sublabel: "Export All Collections", y: 850, icon: Download },
    { id: "page_resetdb", label: "Reset DB", sublabel: "Database Management", y: 940, icon: Trash2 },
  ];

  // Build nodes
  const nodes = [];

  sources.forEach((s) =>
    nodes.push({
      id: s.id,
      type: "flowNode",
      position: { x: col1X, y: s.y },
      data: { ...s, nodeType: "source", hasInput: true, hasOutput: true, id: s.id },
    })
  );
  emails.forEach((s) =>
    nodes.push({
      id: s.id,
      type: "flowNode",
      position: { x: col1X, y: s.y },
      data: { ...s, nodeType: "email", hasOutput: true, id: s.id },
    })
  );
  externals.forEach((s) =>
    nodes.push({
      id: s.id,
      type: "flowNode",
      position: { x: col1X, y: s.y },
      data: { ...s, nodeType: "external", hasOutput: true, id: s.id },
    })
  );
  processing.forEach((s) =>
    nodes.push({
      id: s.id,
      type: "flowNode",
      position: { x: col2X, y: s.y },
      data: { ...s, nodeType: "processing", hasInput: true, hasOutput: true, id: s.id },
    })
  );
  collections.forEach((s) =>
    nodes.push({
      id: s.id,
      type: "flowNode",
      position: { x: col3X, y: s.y },
      data: { ...s, nodeType: "collection", hasInput: true, hasOutput: true, id: s.id, wide: true },
    })
  );
  cronjobs.forEach((s) =>
    nodes.push({
      id: s.id,
      type: "flowNode",
      position: { x: col4X, y: s.y },
      data: { ...s, nodeType: "cronjob", hasInput: true, hasOutput: true, id: s.id },
    })
  );
  pages.forEach((s) =>
    nodes.push({
      id: s.id,
      type: "flowNode",
      position: { x: col5X, y: s.y },
      data: { ...s, nodeType: "page", hasInput: true, id: s.id },
    })
  );

  // Build edges
  const edgeDefs = [
    // Sources → Processing
    ["karvy_mfsd201", "column_mapping"],
    ["karvy_mfsd203", "column_mapping"],
    ["karvy_mfsd211", "column_mapping"],
    ["cams_wbr2", "column_mapping"],
    ["cams_wbr2a", "column_mapping"],
    ["cams_wbr9", "column_mapping"],
    ["cams_wbr9d", "column_mapping"],
    ["karvy_mfsd201", "flag_mapping"],
    ["cams_wbr2", "flag_mapping"],
    // Sources → Raw Ingestion
    ["karvy_mfsd201", "raw_ingestion"],
    ["karvy_mfsd203", "raw_ingestion"],
    ["karvy_mfsd211", "raw_ingestion"],
    ["cams_wbr2", "raw_ingestion"],
    ["cams_wbr2a", "raw_ingestion"],
    ["cams_wbr9", "raw_ingestion"],
    ["cams_wbr9d", "raw_ingestion"],
    // Emails → Processing
    ["karvy_email", "email_processing"],
    ["cams_email", "email_processing"],
    // Email Processing → Raw collections
    ["email_processing", "karvy_mfsd201"],
    ["email_processing", "cams_wbr9"],
    // Processing → Master Collections
    ["column_mapping", "investor_master"],
    ["column_mapping", "aum_master"],
    ["flag_mapping", "transaction_master"],
    ["raw_ingestion", "investor_master"],
    ["raw_ingestion", "aum_master"],
    ["raw_ingestion", "transaction_master"],
    // External → Collections
    ["bse_api", "bse_scheme_raw"],
    ["amfi_api", "scheme_data"],
    ["mfapi", "nav_master"],
    // Cron Jobs → Collections
    ["cron_nav", "nav_master"],
    ["cron_bse", "bse_scheme_raw"],
    ["cron_bse", "scheme_master"],
    ["cron_amfi", "scheme_data"],
    ["cron_amfi", "scheme_master"],
    ["cron_recon", "kyc_mismatch"],
    ["cron_recon", "kyc_missing"],
    // Cron feeds → raw data
    ["cron_karvy_feed", "karvy_mfsd201"],
    ["cron_karvy_feed", "karvy_mfsd203"],
    ["cron_karvy_feed", "karvy_mfsd211"],
    ["cron_cams_feed", "cams_wbr2"],
    ["cron_cams_feed", "cams_wbr9"],
    // Collections → Pages
    ["investor_master", "page_dashboard"],
    ["aum_master", "page_dashboard"],
    ["investor_master", "page_client_wise"],
    ["aum_master", "page_client_wise"],
    ["investor_master", "page_portfolio"],
    ["aum_master", "page_portfolio"],
    ["transaction_master", "page_portfolio"],
    ["nav_master", "page_portfolio"],
    ["kyc_mismatch", "page_mismatch"],
    ["kyc_missing", "page_mismatch"],
    ["aum_master", "page_mismatch"],
    ["investor_master", "page_mismatch"],
    ["scheme_master", "page_research"],
    ["nav_master", "page_research"],
    ["scheme_data", "page_research"],
    // Cron jobs → Cron page
    ["cron_nav", "page_cronjobs"],
    ["cron_bse", "page_cronjobs"],
    ["cron_amfi", "page_cronjobs"],
    ["cron_recon", "page_cronjobs"],
    ["cron_karvy_feed", "page_cronjobs"],
    ["cron_cams_feed", "page_cronjobs"],
    // Masters page sources
    ["column_mapping", "page_masters"],
    ["flag_mapping", "page_masters"],
    ["raw_ingestion", "page_masters"],
    // All collections → Downloads & Reset
    ["investor_master", "page_downloads"],
    ["aum_master", "page_downloads"],
    ["transaction_master", "page_downloads"],
    ["scheme_master", "page_downloads"],
    ["nav_master", "page_downloads"],
    ["bse_scheme_raw", "page_downloads"],
    ["investor_master", "page_resetdb"],
    ["aum_master", "page_resetdb"],
    ["transaction_master", "page_resetdb"],
    ["scheme_master", "page_resetdb"],
    ["nav_master", "page_resetdb"],
    // Cross-collection enrichment
    ["scheme_data", "scheme_master"],
    ["bse_scheme_raw", "scheme_master"],
    ["nav_master", "scheme_master"],
  ];

  const edges = edgeDefs.map(([source, target], i) => ({
    id: `e-${source}-${target}-${i}`,
    source,
    target,
    type: "smoothstep",
    animated: false,
    style: { stroke: "#94a3b8", strokeWidth: 1.2, opacity: 0.5 },
    markerEnd: { type: MarkerType.ArrowClosed, color: "#94a3b8", width: 12, height: 12 },
    data: { sourceId: source, targetId: target },
  }));

  return { nodes, edges, edgeDefs };
}

// ─────────────────────────────────────────────
//  DETAIL PANEL
// ─────────────────────────────────────────────
const DETAIL_INFO = {
  // Sources
  karvy_mfsd201: { title: "MFSD201 (Karvy Transactions)", desc: "Karvy RTA transaction data - contains all mutual fund purchase, redemption, switch, SIP, and dividend transactions processed through Karvy/KFin.", feeds: ["investor_master", "aum_master", "transaction_master"], fedBy: ["Karvy RTA Portal", "Karvy Email Scanner"] },
  karvy_mfsd203: { title: "MFSD203 (Karvy Folios)", desc: "Karvy folio master data with investor details, nominee info, bank details, and KYC status for each folio number.", feeds: ["investor_master"], fedBy: ["Karvy RTA Portal"] },
  karvy_mfsd211: { title: "MFSD211 (Karvy Holdings)", desc: "Current holdings snapshot from Karvy - units held, current value, cost of acquisition per folio-scheme combination.", feeds: ["aum_master"], fedBy: ["Karvy RTA Portal"] },
  cams_wbr2: { title: "WBR2 (CAMS Transactions)", desc: "CAMS RTA transaction data - all mutual fund transactions processed through CAMS registrar.", feeds: ["investor_master", "aum_master", "transaction_master"], fedBy: ["CAMS RTA Portal", "CAMS Email Scanner"] },
  cams_wbr2a: { title: "WBR2A (CAMS Historical)", desc: "Historical CAMS transaction data for computing cost basis and long-term holding analysis.", feeds: ["transaction_master"], fedBy: ["CAMS RTA Portal"] },
  cams_wbr9: { title: "WBR9 (CAMS Holdings)", desc: "Current holdings snapshot from CAMS - live units, NAV, and market value per folio-scheme.", feeds: ["aum_master"], fedBy: ["CAMS RTA Portal", "CAMS Email Scanner"] },
  cams_wbr9d: { title: "WBR9D (CAMS Dividends)", desc: "Dividend/IDCW payout records from CAMS - distribution amounts and reinvestment details.", feeds: ["transaction_master"], fedBy: ["CAMS RTA Portal"] },
  // Emails
  karvy_email: { title: "Karvy Email Scanner", desc: "IMAP-based scanner that monitors mailbox for Karvy RTA attachments (DBF format) and auto-ingests them into raw collections.", feeds: ["karvy_mfsd201", "cams_wbr9"], fedBy: ["Gmail IMAP"] },
  cams_email: { title: "CAMS Email Scanner", desc: "IMAP-based scanner for CAMS RTA email attachments - parses DBF files and feeds into raw data pipeline.", feeds: ["cams_wbr9"], fedBy: ["Gmail IMAP"] },
  // External
  bse_api: { title: "BSE StarMF API", desc: "BSE Star MF scheme master feed with all 43 columns - ISIN codes, scheme names, fund house details, and plan variants.", feeds: ["bse_scheme_raw", "scheme_master"], fedBy: ["BSE India"] },
  amfi_api: { title: "AMFI India API", desc: "Association of Mutual Funds in India - provides official scheme categories, sub-categories, and fund classifications.", feeds: ["scheme_data", "scheme_master"], fedBy: ["amfiindia.com"] },
  mfapi: { title: "mfapi.in API", desc: "Open NAV data API - provides historical and current NAV, face values, and scheme metadata for all mutual funds.", feeds: ["nav_master"], fedBy: ["mfapi.in"] },
  // Processing
  column_mapping: { title: "Column Mapping Engine", desc: "Maps raw Karvy/CAMS column names to a unified schema. Handles 56 mapping configurations for normalizing disparate RTA formats.", feeds: ["investor_master", "aum_master"], fedBy: ["MFSD201", "WBR2", "All raw files"] },
  flag_mapping: { title: "Flag Mapping Engine", desc: "Classifies transaction types (Purchase, Redemption, Switch, SIP, etc.) using flag codes from different RTAs into a unified taxonomy.", feeds: ["transaction_master"], fedBy: ["MFSD201", "WBR2"] },
  raw_ingestion: { title: "Raw Ingestion Pipeline", desc: "Chunked file upload system that handles large files (100MB+) without proxy timeouts. Validates, deduplicates, and routes data to appropriate master collections.", feeds: ["investor_master", "aum_master", "transaction_master"], fedBy: ["All raw data files"] },
  email_processing: { title: "Email Attachment Processor", desc: "Parses DBF format files from email attachments. Handles encoding issues, null bytes, and various DBF field types from Karvy/CAMS.", feeds: ["karvy_mfsd201", "cams_wbr9"], fedBy: ["Karvy Email", "CAMS Email"] },
  // Collections
  investor_master: { title: "Investor Master", desc: "Unified client-folio database combining Karvy & CAMS investor data. Contains PAN, bank details, KYC status, nominee info, holding patterns, and sub-account classification.", feeds: ["Dashboard", "Client Wise", "Portfolio", "Mismatch"], fedBy: ["Column Mapping", "Raw Ingestion"] },
  aum_master: { title: "AUM Master", desc: "Assets Under Management per folio-scheme combination. Contains current units, NAV, market value, cost value, and XIRR calculations.", feeds: ["Dashboard", "Client Wise", "Portfolio", "Mismatch"], fedBy: ["Column Mapping", "Raw Ingestion"] },
  transaction_master: { title: "Transaction Master", desc: "All valid financial transactions across both RTAs. 800K+ records with unified column structure, deduplication via folio+date+app_no+product_code.", feeds: ["Portfolio"], fedBy: ["Flag Mapping", "Raw Ingestion"] },
  scheme_master: { title: "Scheme Master", desc: "Consolidated fund scheme directory enriched with BSE codes, AMFI categories, NAV data, returns, and ISIN mappings.", feeds: ["Research"], fedBy: ["BSE Scheme Master Bot", "AMFI Category Bot", "NAV enrichment"] },
  nav_master: { title: "NAV Master", desc: "Historical & daily NAV repository. Stores 5-day, 3Y, 5Y, and Jan-2018 reference points for XIRR and return calculations.", feeds: ["Portfolio", "Research"], fedBy: ["NAV Update Bot", "mfapi.in"] },
  scheme_data: { title: "Scheme Data (AMFI)", desc: "AMFI-sourced scheme category and sub-category data used to enrich scheme_master with official fund classifications.", feeds: ["scheme_master", "Research"], fedBy: ["AMFI Category Bot"] },
  bse_scheme_raw: { title: "BSE Scheme Raw", desc: "Raw BSE StarMF scheme master data with all 43 columns. Processed via bulk_write for optimal performance.", feeds: ["scheme_master"], fedBy: ["BSE Scheme Master Bot"] },
  kyc_mismatch: { title: "KYC Mismatch", desc: "Records where PAN details differ between Karvy and CAMS for the same investor. Critical for regulatory compliance.", feeds: ["Mismatch page"], fedBy: ["Reconciliation Bot"] },
  kyc_missing: { title: "KYC Missing", desc: "Investors with incomplete or absent KYC documentation across either RTA. Flagged for follow-up and compliance.", feeds: ["Mismatch page"], fedBy: ["Reconciliation Bot"] },
  // Cron Jobs
  cron_nav: { title: "NAV Update Bot", desc: "Scheduled NAV fetcher. First run captures 5-day + 3Y + 5Y + Jan-2018 NAV. Subsequent runs: Daily + 3Y + 5Y points only.", feeds: ["nav_master"], fedBy: ["mfapi.in"] },
  cron_bse: { title: "BSE Scheme Master Bot", desc: "Fetches BSE StarMF scheme data and upserts via bulk_write (13,000+ records in ~56 seconds). Runs at 2:15 AM IST.", feeds: ["bse_scheme_raw", "scheme_master"], fedBy: ["BSE India"] },
  cron_amfi: { title: "AMFI Category Bot", desc: "Fetches and updates AMFI category classifications for all schemes. Runs at 2:00 AM IST.", feeds: ["scheme_data", "scheme_master"], fedBy: ["amfiindia.com"] },
  cron_recon: { title: "Reconciliation Bot", desc: "Compares units across Karvy & CAMS to detect mismatches. Also identifies KYC gaps and PAN discrepancies.", feeds: ["kyc_mismatch", "kyc_missing"], fedBy: ["investor_master", "aum_master"] },
  cron_karvy_feed: { title: "Karvy Feed Bot", desc: "Selenium-based automation that logs into Karvy RTA portal and downloads latest MFSD201/203/211 reports.", feeds: ["MFSD201", "MFSD203", "MFSD211"], fedBy: ["Karvy Portal"] },
  cron_cams_feed: { title: "CAMS Feed Bot", desc: "Selenium-based automation that downloads WBR2/WBR9 reports from the CAMS portal.", feeds: ["WBR2", "WBR9"], fedBy: ["CAMS Portal"] },
  // Pages
  page_dashboard: { title: "Dashboard", desc: "Central analytics hub showing Total Clients, Total Folios, Avg Portfolio, Overall XIRR, CAMS/Karvy AUM breakdown, profit/loss, and reconciliation status.", feeds: [], fedBy: ["investor_master", "aum_master", "dashboard_cache"] },
  page_client_wise: { title: "Client Wise", desc: "Per-client summary view with search, pagination, and drill-down to individual client portfolios.", feeds: [], fedBy: ["investor_master", "aum_master"] },
  page_portfolio: { title: "Portfolio", desc: "Comprehensive portfolio view with tabs: Active Units, All Transactions, Sold, Non-Financial, IDCW Payouts, Unit Mismatch. Sub-account filtering for Minors, Joint Holders, NRIs, Firms.", feeds: [], fedBy: ["investor_master", "aum_master", "transaction_master", "nav_master"] },
  page_mismatch: { title: "Mismatch", desc: "KYC reconciliation dashboard showing PAN mismatches, missing KYC records, and unit discrepancies between Karvy and CAMS.", feeds: [], fedBy: ["kyc_mismatch", "kyc_missing", "aum_master", "investor_master"] },
  page_research: { title: "Research (MF Schemes)", desc: "Mutual fund scheme browser with NAV charts, return comparisons, category filters, and scheme analytics.", feeds: [], fedBy: ["scheme_master", "nav_master", "scheme_data"] },
  page_cronjobs: { title: "Cron Jobs", desc: "Bot scheduling and monitoring UI. Manages NAV updates, BSE/AMFI sync, Karvy/CAMS feed bots, and reconciliation runs.", feeds: [], fedBy: ["All cron jobs"] },
  page_masters: { title: "Masters", desc: "Data upload hub for Karvy/CAMS files. Manages column mappings, flag mappings, and transaction master generation.", feeds: [], fedBy: ["column_mapping", "flag_mapping", "raw_ingestion"] },
  page_downloads: { title: "Downloads", desc: "Export center for all collections as Excel files. Supports investor_master, aum_master, transaction_master, scheme data, and more.", feeds: [], fedBy: ["All master collections"] },
  page_resetdb: { title: "Reset DB", desc: "Database management interface for clearing or dropping individual collections. Secured for broker role only.", feeds: [], fedBy: ["All collections"] },
};

function DetailPanel({ nodeId, onClose }) {
  const info = DETAIL_INFO[nodeId];
  if (!info) return null;
  const style = NODE_STYLES[
    nodeId.startsWith("page_") ? "page" :
    nodeId.startsWith("cron_") ? "cronjob" :
    ["investor_master","aum_master","transaction_master","scheme_master","nav_master","scheme_data","bse_scheme_raw","kyc_mismatch","kyc_missing"].includes(nodeId) ? "collection" :
    ["column_mapping","flag_mapping","raw_ingestion","email_processing"].includes(nodeId) ? "processing" :
    ["karvy_email","cams_email"].includes(nodeId) ? "email" :
    ["bse_api","amfi_api","mfapi"].includes(nodeId) ? "external" : "source"
  ];

  return (
    <div
      data-testid="detail-panel"
      className="absolute right-4 top-4 z-50 w-80 rounded-xl overflow-hidden"
      style={{ background: "#ffffff", border: `1px solid ${style.border}`, boxShadow: "0 8px 30px rgba(0,0,0,0.12)" }}
    >
      <div className="p-4" style={{ borderBottom: `1px solid #e2e8f0` }}>
        <div className="flex items-center justify-between mb-2">
          <Badge style={{ background: `${style.accent}15`, color: style.accent, border: `1px solid ${style.accent}30` }}>
            {NODE_STYLES[
              nodeId.startsWith("page_") ? "page" :
              nodeId.startsWith("cron_") ? "cronjob" :
              ["investor_master","aum_master","transaction_master","scheme_master","nav_master","scheme_data","bse_scheme_raw","kyc_mismatch","kyc_missing"].includes(nodeId) ? "collection" :
              ["column_mapping","flag_mapping","raw_ingestion","email_processing"].includes(nodeId) ? "processing" :
              ["karvy_email","cams_email"].includes(nodeId) ? "email" :
              ["bse_api","amfi_api","mfapi"].includes(nodeId) ? "external" : "source"
            ]?.label}
          </Badge>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={16} />
          </button>
        </div>
        <h3 className="text-gray-800 font-semibold text-sm">{info.title}</h3>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-gray-500 text-xs leading-relaxed">{info.desc}</p>
        {info.feeds.length > 0 && (
          <div>
            <p className="text-gray-400 text-[10px] uppercase tracking-wider mb-1 font-semibold">Feeds Into</p>
            <div className="flex flex-wrap gap-1">
              {info.feeds.map((f) => (
                <span key={f} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "#16a34a10", color: "#16a34a", border: "1px solid #16a34a25" }}>
                  {f}
                </span>
              ))}
            </div>
          </div>
        )}
        {info.fedBy.length > 0 && (
          <div>
            <p className="text-gray-400 text-[10px] uppercase tracking-wider mb-1 font-semibold">Sourced From</p>
            <div className="flex flex-wrap gap-1">
              {info.fedBy.map((f) => (
                <span key={f} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "#2563eb10", color: "#2563eb", border: "1px solid #2563eb25" }}>
                  {f}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  LEGEND
// ─────────────────────────────────────────────
function Legend() {
  return (
    <div className="flex flex-wrap gap-3 items-center">
      {Object.entries(NODE_STYLES).map(([key, val]) => (
        <div key={key} className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ background: val.accent }} />
          <span className="text-[11px] text-gray-500">{val.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────
export default function DataFlow() {
  const { token } = useAuth();
  const [collectionCounts, setCollectionCounts] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  const [highlightedNodes, setHighlightedNodes] = useState(new Set());
  const [highlightedEdges, setHighlightedEdges] = useState(new Set());

  // Fetch collection counts
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/api/database/collections-info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          // API returns {collections: {name: count, ...}} object format
          const collectionsData = data.collections || data;
          if (collectionsData && typeof collectionsData === 'object' && !Array.isArray(collectionsData)) {
            // Object format: {MFSD201: 362834, ...}
            setCollectionCounts(collectionsData);
          } else if (Array.isArray(collectionsData)) {
            // Array format: [{name, count}, ...]
            const counts = {};
            collectionsData.forEach((c) => {
              counts[c.name] = c.count;
            });
            setCollectionCounts(counts);
          }
        }
      } catch (err) {
        console.error("Failed to fetch collection counts", err);
      }
    })();
  }, [token]);

  const { nodes: initialNodes, edges: initialEdges, edgeDefs } = useMemo(
    () => buildGraph(collectionCounts),
    [collectionCounts]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Rebuild when collection counts change
  useEffect(() => {
    const { nodes: n, edges: e } = buildGraph(collectionCounts);
    setNodes(n);
    setEdges(e);
  }, [collectionCounts, setNodes, setEdges]);

  // Find connected nodes (upstream + downstream)
  const getConnectedIds = useCallback(
    (nodeId) => {
      const connected = new Set([nodeId]);
      const connectedEdgeIds = new Set();

      // BFS upstream
      const upQueue = [nodeId];
      while (upQueue.length) {
        const current = upQueue.shift();
        edgeDefs.forEach(([src, tgt], i) => {
          if (tgt === current && !connected.has(src)) {
            connected.add(src);
            connectedEdgeIds.add(`e-${src}-${tgt}-${i}`);
            upQueue.push(src);
          }
        });
      }

      // BFS downstream
      const downQueue = [nodeId];
      while (downQueue.length) {
        const current = downQueue.shift();
        edgeDefs.forEach(([src, tgt], i) => {
          if (src === current && !connected.has(tgt)) {
            connected.add(tgt);
            connectedEdgeIds.add(`e-${src}-${tgt}-${i}`);
            downQueue.push(tgt);
          }
        });
      }

      // Also add direct edges for the selected node
      edgeDefs.forEach(([src, tgt], i) => {
        if (connected.has(src) && connected.has(tgt)) {
          connectedEdgeIds.add(`e-${src}-${tgt}-${i}`);
        }
      });

      return { connected, connectedEdgeIds };
    },
    [edgeDefs]
  );

  // Handle node click
  const onNodeClick = useCallback(
    (_, node) => {
      if (selectedNode === node.id) {
        // Deselect
        setSelectedNode(null);
        setHighlightedNodes(new Set());
        setHighlightedEdges(new Set());
        setNodes((nds) =>
          nds.map((n) => ({ ...n, data: { ...n.data, highlighted: false, dimmed: false } }))
        );
        setEdges((eds) =>
          eds.map((e) => ({
            ...e,
            animated: false,
            style: { ...e.style, stroke: "#94a3b8", strokeWidth: 1.2, opacity: 0.5 },
            markerEnd: { ...e.markerEnd, color: "#94a3b8" },
          }))
        );
        return;
      }

      const { connected, connectedEdgeIds } = getConnectedIds(node.id);
      setSelectedNode(node.id);
      setHighlightedNodes(connected);
      setHighlightedEdges(connectedEdgeIds);

      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          data: {
            ...n.data,
            highlighted: connected.has(n.id),
            dimmed: !connected.has(n.id),
          },
        }))
      );

      const nodeStyle = NODE_STYLES[node.data.nodeType] || NODE_STYLES.collection;
      setEdges((eds) =>
        eds.map((e) => {
          const isConnected = connectedEdgeIds.has(e.id);
          return {
            ...e,
            animated: isConnected,
            style: {
              ...e.style,
              stroke: isConnected ? nodeStyle.accent : "#94a3b8",
              strokeWidth: isConnected ? 2.5 : 1.2,
              opacity: isConnected ? 0.9 : 0.1,
            },
            markerEnd: {
              ...e.markerEnd,
              color: isConnected ? nodeStyle.accent : "#94a3b8",
            },
          };
        })
      );
    },
    [selectedNode, getConnectedIds, setNodes, setEdges]
  );

  // Click on background to deselect
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setHighlightedNodes(new Set());
    setHighlightedEdges(new Set());
    setNodes((nds) =>
      nds.map((n) => ({ ...n, data: { ...n.data, highlighted: false, dimmed: false } }))
    );
    setEdges((eds) =>
      eds.map((e) => ({
        ...e,
        animated: false,
        style: { ...e.style, stroke: "#94a3b8", strokeWidth: 1.2, opacity: 0.5 },
        markerEnd: { ...e.markerEnd, color: "#94a3b8" },
      }))
    );
  }, [setNodes, setEdges]);

  return (
    <div data-testid="data-flow-page" className="relative" style={{ height: "calc(100vh - 48px)" }}>
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 px-4 py-3" style={{ background: "linear-gradient(to bottom, #f0fdf8, #f0fdf8ee, transparent)" }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Layers className="h-5 w-5 text-teal-600" />
              System Data Flow
            </h1>
            <p className="text-gray-400 text-xs mt-0.5">Click any node to trace its data dependencies</p>
          </div>
          <Legend />
        </div>
      </div>

      {/* React Flow Canvas */}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.2}
        maxZoom={2}
        style={{ background: "#f0fdf8" }}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#c2e9e0" gap={24} size={1.5} />
        <Controls
          style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8 }}
          showInteractive={false}
        />
        <MiniMap
          nodeColor={(n) => {
            const st = NODE_STYLES[n.data?.nodeType];
            return st ? st.accent : "#94a3b8";
          }}
          style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8 }}
          maskColor="#f0fdf890"
        />
      </ReactFlow>

      {/* Detail Panel */}
      {selectedNode && (
        <DetailPanel nodeId={selectedNode} onClose={() => {
          setSelectedNode(null);
          onPaneClick();
        }} />
      )}
    </div>
  );
}
